<template>
  <div class="mpf-grid-row">
    <!-- The column is its own element, as on every other step: putting the grid
         class on MpfForm made the form the column, 32px wider than the content
         beside it, so the page jumped between steps. -->
    <div class="mpf-grid-column-full">
      <!-- No form-title: the route's layoutPageTitle already renders the h1, and
           passing both stacks two headings at the same size. -->
      <MpfForm
        :hint="formPatientDetailsYou.config.hint"
        :fields="formPatientDetailsYou.fields"
        :form-errors="formErrors"
        submit-label="Continue"
        @submit="handleSubmit"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { onMounted, ref } from 'vue';
  import { useRouter } from 'vue-router';

  import MpfForm from '../components/MpfForm/MpfForm.vue';
  import type { FormError } from '../components/MpfForm/MpfForm.props';
  import { formPatientDetailsYou } from '../../data/forms';
  import { useAppointmentBookingStore } from '../../stores/useAppointmentBookingStore';
  import { useErrorStore } from '../../stores/useErrorStore';
  import { useBookingLinkRoute } from '../composables/useBookingLinkRoute';

  const router = useRouter();
  const store = useAppointmentBookingStore();
  const errorStore = useErrorStore();

  const { bookingLinkId, pathForStep } = useBookingLinkRoute();
  const formErrors = ref<FormError[]>([]);

  // The link's three flags decide the rest of the chain, so it is resolved
  // before the first step rather than at the step that needs each flag.
  onMounted(async () => {
    if (bookingLinkId.value && !store.linkType) await store.loadLink(bookingLinkId.value);
  });

  const handleSubmit = async (data: { valid: boolean; values: Record<string, any> }) => {
    if (!data.valid) return;

    formErrors.value = [];

    // Three-point match first: nothing about the diary is shown until the details
    // resolve to a patient, and this mints the session the later calls read.
    const result = await store.authenticate(data.values as never);

    if (!result.ok) {
      errorStore.setErrors(result.errors);
      formErrors.value = [
        {
          name: 'firstName',
          errors: [
            errorStore.getFirstMessage() ||
              'We could not find your details. Check them and try again.',
          ],
        },
      ];
      return;
    }

    const next = store.nextStep('appointment-booking-patient-details');
    if (!next) return;

    await router.push(pathForStep(next));
  };
</script>
